import z from 'zod';

import {
  LoanLateFeeType,
  LoanPrincipalLateFeeType,
  LoanTerminationFeeType,
  PrepaymentPenaltyPlanType,
} from '../../../loan/LoanFees';
import {
  Checkbox,
  NonNegativeFloat,
  NonNegativeInteger,
  NonNegativeMonetaryValue,
  NonNegativePercentage,
  PositiveInteger,
} from '../../fields';

export const PrepaymentPenaltyFeeOverrideFields = z.object({
  prepaymentPenaltyFlag: Checkbox.optional(),
  prepaymentPenaltyExpirationMonths: NonNegativeInteger.optional(),
  prepaymentPenaltyPlanDescription: z.string().optional(),
  prepaymentPenaltyPlanType: PrepaymentPenaltyPlanType.optional(),
});
export type PrepaymentPenaltyFeeOverrideFields = z.infer<typeof PrepaymentPenaltyFeeOverrideFields>;

export const LoanFeeOverrideFields = z.object({
  ...PrepaymentPenaltyFeeOverrideFields.shape,

  /* LATE FEE */
  lateFeeType: LoanLateFeeType.optional().describe(
    'Optionally override the company-level setting for applying late fees',
  ),
  lateFeePercent: NonNegativePercentage.optional().describe(
    'If lateFeeType is set to "percent", use lateFeePercent OR lateFeeDecimal. Example: 6',
  ),
  lateFeeDecimal: NonNegativeFloat.optional().describe(
    'If lateFeeType is set to "percent", use lateFeePercent OR lateFeeDecimal. Example: 0.06',
  ),
  lateFeeMin: NonNegativeMonetaryValue.optional().describe('Optional if lateFeeType is set to "percent"'),
  lateFeeMax: NonNegativeMonetaryValue.optional().describe('Optional if lateFeeType is set to "percent"'),
  lateFeeFixedAmount: NonNegativeMonetaryValue.optional().describe(
    'This field is required if lateFeeType is set to "fixed"',
  ),

  /* TERMINATION FEE */ terminationFeeFlag: Checkbox.optional(),
  terminationFeeType: LoanTerminationFeeType.optional().describe(
    'If terminationFeeFlag is true, this field is required',
  ),
  terminationFeePercent: NonNegativePercentage.optional().describe(
    'If terminationFeeType is set to "percent", this field is required',
  ),
  terminationFeePercentMonths: PositiveInteger.optional().describe(
    'If terminationFeeType is set to "percent", this field is required',
  ),
  terminationFeeFixedAmount: NonNegativeMonetaryValue.optional().describe(
    'If terminationFeeType is set to "fixed", this field is required',
  ),
  terminationFeeFixedMonths: PositiveInteger.optional().describe(
    'If terminationFeeType is set to "fixed", this field is required',
  ),
  terminationFeePerProperty: Checkbox.optional().describe(
    'If terminationFeeType is set to "fixed", this field is required',
  ),

  /* PRINCIPAL LATE FEE */
  principalLateFeeFlag: Checkbox.optional(),
  principalLateFeeType: LoanPrincipalLateFeeType.optional().describe(
    'If principalLateFeeFlag is true, this field is required',
  ),
  principalLateFeePercent: NonNegativePercentage.optional().describe(
    'If principalLateFeeType is set to "percent", this field is required',
  ),
  principalLateFeePercentDays: PositiveInteger.optional().describe(
    'If principalLateFeeType is set to "percent", this field is required',
  ),
  principalLateFeeFixedAmount: NonNegativeMonetaryValue.optional().describe(
    'If principalLateFeeType is set to "fixed", this field is required',
  ),
  principalLateFeeFixedDays: PositiveInteger.optional().describe(
    'If principalLateFeeType is set to "fixed", this field is required',
  ),
  principalLateFeePerProperty: Checkbox.optional().describe(
    'If principalLateFeeType is set to "fixed", this field is required',
  ),
});
export type LoanFeeOverrideFields = z.infer<typeof LoanFeeOverrideFields>;
